/** `residencyRegion` value that gates storage-backed console surfaces. */
export const TENANT_NO_STORAGE_RESIDENCY_REGION = 'no-storage'

/** `labelKey` is a key in the `common` translations. */
export type StorageLocationOption = {labelKey: string; value: string}

/**
 * Data residency choices, in dropdown order. A `value` lower-cased is the
 * `country` sent to POST /data-residency and the `residencyRegion` that
 * tenant config returns.
 */
export const STORAGE_LOCATION_OPTIONS = {
  USA: {labelKey: 'STORAGE_LOCATION_USA', value: 'USA'},
  GERMANY: {labelKey: 'STORAGE_LOCATION_GERMANY', value: 'Germany'},
  BAHRAIN: {labelKey: 'STORAGE_LOCATION_BAHRAIN', value: 'Bahrain'},
  SINGAPORE: {labelKey: 'STORAGE_LOCATION_SINGAPORE', value: 'Singapore'},
  AUSTRALIA: {labelKey: 'STORAGE_LOCATION_AUSTRALIA', value: 'Australia'},
  BRAZIL: {labelKey: 'STORAGE_LOCATION_BRAZIL', value: 'Brazil'},
  NO_STORAGE: {
    labelKey: 'STORAGE_LOCATION_NO_STORAGE',
    value: TENANT_NO_STORAGE_RESIDENCY_REGION,
  },
} as const

/** Every location a tenant can store records in (No Storage excluded). */
export const STORAGE_COUNTRY_OPTIONS: StorageLocationOption[] = Object.values(
  STORAGE_LOCATION_OPTIONS,
).filter(option => option.value !== TENANT_NO_STORAGE_RESIDENCY_REGION)

/** The `residencyRegion` / `country` form of a storage location value. */
export const toResidencyRegion = (value: string): string =>
  value.toLocaleLowerCase()

/** Translation key for a tenant's `residencyRegion`, if it is a known one. */
export const getStorageLocationLabelKey = (
  residencyRegion: string,
): string | undefined =>
  Object.values(STORAGE_LOCATION_OPTIONS).find(
    option => toResidencyRegion(option.value) === residencyRegion,
  )?.labelKey

/**
 * Router state that links to Subscription send (for example the Analytics
 * "Enable storage" link) so the Data residency section draws attention to
 * itself and focuses the location dropdown on arrival.
 */
export interface DataResidencyFocusState {
  focusDataResidency: true
}

export const DATA_RESIDENCY_FOCUS_STATE: DataResidencyFocusState = {
  focusDataResidency: true,
}

export const isDataResidencyFocusRequested = (
  state: unknown,
): state is DataResidencyFocusState =>
  typeof state === 'object' &&
  state !== null &&
  (state as Partial<DataResidencyFocusState>).focusDataResidency === true
